import { useCurrentUser, useToastProvider } from '@qualio/ui-components';
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonGroupContext } from '../../../../Contexts/ButtonGroupContext';
import { DocumentConvertFormatModal } from '../../../../DocumentConvertFormatModal/index';
import { DocumentOverviewContext } from '../../../Context';
import { shouldRenderChangeFormatButton } from '../../../RenderLogic';

type ChangeFormatButtonProps = {
  renderClickableItem: (onClick: () => void) => JSX.Element;
};

export const ChangeFormatButton = ({
  renderClickableItem,
}: ChangeFormatButtonProps) => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const { qualioDocument, refetchDocumentOverviewData } = useContext(
    DocumentOverviewContext,
  );
  const { setDisabled } = useContext(ButtonGroupContext);
  const { showToast } = useToastProvider();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const canChangeFormat = useMemo(
    () => shouldRenderChangeFormatButton(qualioDocument, currentUser),
    [currentUser, qualioDocument],
  );

  const onSuccess = () => {
    refetchDocumentOverviewData();
    showToast({
      'data-cy':
        'change-format-button-doc-overview-change-format-success-toast',
      id: 'doc-overview-change-format-toast',
      replace: true,
      status: 'success',
      title: t('notifications.documentFormatChanged'),
      description: t('success.successfullyChangedDocumentFormat'),
    });
    setDisabled(false);
  };

  const onError = () => {
    setDisabled(false);
  };

  if (!canChangeFormat) {
    return null;
  }

  return (
    <>
      {renderClickableItem(() => {
        setDisabled(true);
        setIsModalOpen(true);
      })}
      {isModalOpen && (
        <DocumentConvertFormatModal
          onClose={() => setDisabled(false, setIsModalOpen)}
          qualioDocument={qualioDocument}
          onSuccess={onSuccess}
          onError={onError}
        />
      )}
    </>
  );
};
